import { useState, useMemo, useCallback, useEffect } from 'react';
import { SubscriptionSummary } from '../../../../../types/subscription';
import { DepositEntry } from '../../../types';
import { Exchange } from 'grts-utils/types/Exchange';

function buildEntries(
  subscriptions: SubscriptionSummary[],
  selectedExchange: Exchange | null,
): DepositEntry[] {
  return subscriptions
    .filter((s) => s.exchange === selectedExchange)
    .map((s) => ({
      subscriptionId: s.id,
      botFriendlyName: s.botFriendlyName,
      baseAsset: s.baseAsset,
      quoteAsset: s.quoteAsset,
      exchange: s.exchange,
      currentStableBalance: s.stableBalance,
      currentTotalBalance: s.totalBalanceInStable,
      depositAmount: 0,
      depositPercentage: 0,
      isLocked: true,
    }));
}

export function useDepositDistributionState(
  subscriptions: SubscriptionSummary[],
  selectedExchange: Exchange | null,
  availableBalances: Record<string, number>,
) {
  const [entries, setEntries] = useState<DepositEntry[]>(() =>
    buildEntries(subscriptions, selectedExchange),
  );

  useEffect(() => {
    setEntries(buildEntries(subscriptions, selectedExchange));
  }, [subscriptions, selectedExchange]);

  const hasChanges = useMemo(
    () => entries.some((e) => e.depositAmount > 0.01),
    [entries],
  );

  const changedEntries = useMemo(
    () => entries.filter((e) => e.depositAmount > 0.01),
    [entries],
  );

  const allocatedByQuoteAsset = useMemo(() => {
    return entries.reduce<Record<string, number>>((acc, e) => {
      acc[e.quoteAsset] = (acc[e.quoteAsset] ?? 0) + e.depositAmount;
      return acc;
    }, {});
  }, [entries]);

  const setDepositAmount = useCallback(
    (subscriptionId: number, amount: number) => {
      setEntries((prev) => {
        const entry = prev.find((e) => e.subscriptionId === subscriptionId);
        if (!entry || entry.isLocked) return prev;

        const { quoteAsset } = entry;
        const available = availableBalances[quoteAsset] ?? 0;
        const otherAllocated = prev
          .filter(
            (e) =>
              e.quoteAsset === quoteAsset &&
              e.subscriptionId !== subscriptionId,
          )
          .reduce((sum, e) => sum + e.depositAmount, 0);

        const max = Math.max(0, available - otherAllocated);
        const clamped = Math.max(0, Math.min(amount, max));

        return prev.map((e) =>
          e.subscriptionId === subscriptionId
            ? {
                ...e,
                depositAmount: clamped,
                depositPercentage:
                  available > 0 ? (clamped / available) * 100 : 0,
              }
            : e,
        );
      });
    },
    [availableBalances],
  );

  const setDepositPercentage = useCallback(
    (subscriptionId: number, pct: number) => {
      const entry = entries.find((e) => e.subscriptionId === subscriptionId);
      if (!entry) return;
      const available = availableBalances[entry.quoteAsset] ?? 0;
      const amount = (Math.max(0, Math.min(100, pct)) / 100) * available;
      setDepositAmount(subscriptionId, amount);
    },
    [availableBalances, entries, setDepositAmount],
  );

  const toggleLock = useCallback((subscriptionId: number) => {
    setEntries((prev) =>
      prev.map((e) =>
        e.subscriptionId === subscriptionId
          ? { ...e, isLocked: !e.isLocked }
          : e,
      ),
    );
  }, []);

  const reset = useCallback(() => {
    setEntries((prev) =>
      prev.map((e) => ({ ...e, depositAmount: 0, depositPercentage: 0 })),
    );
  }, []);

  return {
    entries,
    allocatedByQuoteAsset,
    hasChanges,
    changedEntries,
    setDepositAmount,
    setDepositPercentage,
    toggleLock,
    reset,
  };
}
